import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { Key, Copy, Trash2, Plus, Loader2, Eye, EyeOff, Code, ExternalLink, History, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APIIntegrationPanel = () => {
  const { user, getAuthHeader } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [showDocs, setShowDocs] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchOutlets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/outlets`, { headers: getAuthHeader() });
      setOutlets(res.data);
      if (res.data.length > 0 && !selectedOutlet) {
        setSelectedOutlet(res.data[0].id);
      }
    } catch (e) {
      console.error('Failed to fetch outlets:', e);
    }
  }, [getAuthHeader, selectedOutlet]);

  const fetchApiKeys = useCallback(async () => {
    if (!selectedOutlet) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/outlets/${selectedOutlet}/api-keys`, { headers: getAuthHeader() });
      setApiKeys(res.data);
    } catch (e) {
      console.error('Failed to fetch API keys:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedOutlet, getAuthHeader]);

  useEffect(() => { fetchOutlets(); }, [fetchOutlets]);
  useEffect(() => { fetchApiKeys(); }, [fetchApiKeys]);

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Enter a name for the API key');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/outlets/${selectedOutlet}/api-keys`, { name: newKeyName }, { headers: getAuthHeader() });
      toast.success('API key created');
      setNewKeyName('');
      fetchApiKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const deleteKey = async (keyId) => {
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { headers: getAuthHeader() });
      toast.success('API key deleted');
      fetchApiKeys();
    } catch (e) {
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const fetchApiLogs = useCallback(async () => {
    if (!selectedOutlet) return;
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API}/api-logs/${selectedOutlet}?limit=30`, { headers: getAuthHeader() });
      setApiLogs(res.data);
    } catch (e) {
      console.error('Failed to fetch API logs:', e);
    } finally {
      setLogsLoading(false);
    }
  }, [selectedOutlet, getAuthHeader]);

  useEffect(() => {
    if (showLogs) fetchApiLogs();
  }, [showLogs, fetchApiLogs]);

  const outletName = outlets.find(o => o.id === selectedOutlet)?.name || '';
  const apiBaseUrl = process.env.REACT_APP_BACKEND_URL;

  const sampleCurl = `curl -X POST "${apiBaseUrl}/api/external/orders" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "your_name": "John Doe",
    "delivery_address": "123 Main Street",
    "road": "Riverside Road",
    "phone_number": "+1234567890",
    "payment_method": "cash",
    "item": "2x Burger, 1x Fries",
    "quantity": 3,
    "package_information": "Fragile, handle with care",
    "launch_name": "Express Launch",
    "captain_number": "CPT-001",
    "receiver_number": "+0987654321",
    "launch_departure_location": "Dock A",
    "launch_arrival_date": "2026-03-15",
    "launch_arrival_time": "14:00",
    "launch_departure_date": "2026-03-15",
    "launch_departure_time": "10:00"
  }'`;

  return (
    <div className="space-y-5">
      {/* Outlet Selector */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Select Outlet</Label>
        <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
          <SelectTrigger data-testid="api-outlet-selector">
            <SelectValue placeholder="Select an outlet" />
          </SelectTrigger>
          <SelectContent>
            {outlets.map((outlet) => (
              <SelectItem key={outlet.id} value={outlet.id}>{outlet.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedOutlet && (
        <>
          {/* Create New Key */}
          <div className="flex gap-2">
            <Input
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g., annisa.app production)"
              className="flex-1"
              data-testid="api-key-name-input"
            />
            <Button onClick={createKey} disabled={creating} size="sm" data-testid="generate-api-key-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Generate
            </Button>
          </div>

          {/* API Keys List */}
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Key className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No API keys for this outlet yet</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[180px]">
              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-border" data-testid={`api-key-item-${key.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{key.name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <code className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">
                          {visibleKeys[key.id] ? key.api_key : `${key.api_key.slice(0, 8)}${'*'.repeat(20)}`}
                        </code>
                      </div>
                      {key.last_used_at && (
                        <p className="text-[11px] text-muted-foreground mt-1">Last used: {new Date(key.last_used_at).toLocaleString()}</p>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => toggleKeyVisibility(key.id)}>
                      {visibleKeys[key.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => copyToClipboard(key.api_key)} data-testid={`copy-key-${key.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-red-500 hover:text-red-600" onClick={() => deleteKey(key.id)} data-testid={`delete-key-${key.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {/* API Docs Toggle */}
          <Button variant="outline" size="sm" className="w-full" onClick={() => setShowDocs(!showDocs)} data-testid="toggle-api-docs">
            <Code className="h-4 w-4 mr-2" />
            {showDocs ? 'Hide' : 'Show'} API Documentation
          </Button>

          {showDocs && (
            <div className="space-y-4 bg-muted/30 p-4 rounded-lg border border-border">
              {/* Endpoint */}
              <div>
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Endpoint</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm bg-background px-3 py-1.5 rounded border font-mono flex-1 truncate">
                    POST {apiBaseUrl}/api/external/orders
                  </code>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => copyToClipboard(`${apiBaseUrl}/api/external/orders`)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Field Mapping */}
              <div>
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Field Mapping (annisa.app → Deliman)</Label>
                <div className="mt-2 text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1.5 font-semibold">Website Field</th>
                        <th className="text-left py-1.5 font-semibold">API Field</th>
                        <th className="text-left py-1.5 font-semibold">Deliman</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b border-border/50"><td className="py-1">Your Name</td><td><code>your_name</code></td><td>Metadata</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Delivery Address</td><td><code>delivery_address</code></td><td>Location</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Road</td><td><code>road</code></td><td>Location</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Launch Departure Location</td><td><code>launch_departure_location</code></td><td>Location</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Phone Number</td><td><code>phone_number</code></td><td>Contact No</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Payment Method</td><td><code>payment_method</code></td><td>Payment Method</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Item</td><td><code>item</code></td><td>Item Details</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Quantity</td><td><code>quantity</code></td><td>Quantity</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Package Information</td><td><code>package_information</code></td><td>Customer Notes</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Launch Name</td><td><code>launch_name</code></td><td>Metadata</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Captain Number</td><td><code>captain_number</code></td><td>Metadata</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Receiver Number</td><td><code>receiver_number</code></td><td>Metadata</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1">Launch Arrival Date/Time</td><td><code>launch_arrival_date/time</code></td><td>Metadata</td></tr>
                      <tr><td className="py-1">Launch Departure Date/Time</td><td><code>launch_departure_date/time</code></td><td>Metadata</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Sample cURL */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sample Request</Label>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => copyToClipboard(sampleCurl)}>
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                </div>
                <pre className="mt-1 text-[11px] bg-background p-3 rounded border overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap break-all">
                  {sampleCurl}
                </pre>
              </div>

              {/* Additional Endpoints */}
              <div>
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Other Endpoints</Label>
                <div className="mt-1 text-xs text-muted-foreground space-y-1">
                  <p><code className="bg-background px-1.5 py-0.5 rounded">GET /api/external/orders</code> — List orders</p>
                  <p><code className="bg-background px-1.5 py-0.5 rounded">GET /api/external/orders/:id</code> — Get order detail</p>
                  <p><code className="bg-background px-1.5 py-0.5 rounded">PUT /api/external/orders/:id/status</code> — Update order status</p>
                  <p><code className="bg-background px-1.5 py-0.5 rounded">GET /api/external/stats</code> — Get outlet statistics</p>
                </div>
              </div>
            </div>
          )}

          {/* Webhook History Toggle */}
          <Button variant="outline" size="sm" className="w-full" onClick={() => setShowLogs(!showLogs)} data-testid="toggle-api-logs">
            <History className="h-4 w-4 mr-2" />
            {showLogs ? 'Hide' : 'Show'} Request History
          </Button>

          {showLogs && (
            <div className="space-y-2 bg-muted/30 p-4 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recent API Requests</Label>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={fetchApiLogs} disabled={logsLoading}>
                  {logsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
              {logsLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
              ) : apiLogs.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No API requests recorded yet</p>
              ) : (
                <ScrollArea className="max-h-[250px]">
                  <div className="space-y-1.5">
                    {apiLogs.map((log) => (
                      <div key={log.id} className="flex items-center gap-2 p-2 bg-background rounded border text-xs" data-testid={`api-log-${log.id}`}>
                        {log.status_code < 400 ? (
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        )}
                        <span className="font-mono font-semibold text-blue-500">{log.method}</span>
                        <span className="font-mono text-muted-foreground truncate flex-1">{log.endpoint}</span>
                        <span className={`font-mono ${log.status_code < 400 ? 'text-emerald-500' : 'text-red-500'}`}>{log.status_code}</span>
                        <span className="text-muted-foreground/60 shrink-0">{new Date(log.created_at).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default APIIntegrationPanel;
